/**
 * API endpoints for the patient record — OWNED BY THIS MODULE.
 *
 * Deliberately NOT added to src/service/network/APIConfig.js. This module is a
 * vendored copy of phr's patient dashboard and is meant to be deletable: remove
 * this directory and the one route that mounts it, and bridge is exactly as it
 * was. Putting 20 endpoint blocks into the shared config would have made that
 * untrue, and would have edited a file every other screen in the app depends on.
 *
 * BASE URLS: the twelve "v2" patient-record services (api-cc-v2-service,
 * api-diagnosis-v2-service, ...) are one gateway, not twelve hosts — in every
 * environment they resolve to the same value as bridge's own serviceUrl. They
 * are still listed individually below because that is what lets a developer
 * point ONE service at localhost without moving the other eleven.
 *
 * Environment values come from bridge's EnvironmentService, read-only. We do
 * not extend it: aiming a v2 call at a different host than the rest of bridge
 * means aiming it at a different Keycloak subject, which surfaces as a 401 here
 * that 200s there.
 */
import EnvironmentService from "service/environment/EnvironmentService";

const environmentService = new EnvironmentService();
const baseAPI = environmentService.getBaseAPI();
const serviceAPI = environmentService.getServiceAPI();

/* One gateway today. To repoint a single service locally, change its line. */
const ccV2API = serviceAPI;
const diagnosisV2API = serviceAPI;
const medicineV2API = serviceAPI;
const addictionV2API = serviceAPI;
const familyHistoryV2API = serviceAPI;
const activityV2API = serviceAPI;
const allergyV2API = serviceAPI;
const hospitalisationV2API = serviceAPI;
const labInvestigationV2API = serviceAPI;
const anthropometricV2API = serviceAPI;
const foodPreferencesV2API = serviceAPI;
const dailyScheduleV2API = serviceAPI;

const PatientAPIConfig = {
  /**
   * Activity catalog — fuzzy-search endpoint served by the same service. The
   * frontend picker calls this on debounced input and snapshots the picked
   * entry (name + category) into the Activity record on the patient's doc.
   * Returns { data: [{ id, name, category }] }.
   */
  activityCatalog: {
    search: activityV2API + "/api/v1/activity-catalog?search={0}&limit=20",
  },
  /**
   * Physical Activity v2 (api-activity-v2-service). Sixth v2 service.
   * Two POSTs — full-detail Update (edit-in-place) and status-only Update
   * (used by the status chip modal). Delete is soft.
   */
  activityV2: {
    list:                  activityV2API + "/api/v1/activities/{0}",
    add:                   activityV2API + "/api/v1/activities/{0}",
    update:                activityV2API + "/api/v1/activities/{0}/{1}",
    updateStatus:          activityV2API + "/api/v1/activities/{0}/{1}/status",
    delete:                activityV2API + "/api/v1/activities/{0}/{1}",
    trend:                 activityV2API + "/api/v1/activities/{0}/{1}/trend",
    markHistoryIncorrect:  activityV2API + "/api/v1/activities/{0}/{1}/history/{2}/incorrect",
  },
  /**
   * Addiction v2 (api-addiction-v2-service). Fourth v2 service — different
   * shape from CC / Diagnosis / Medicine: the entry set is fixed 4 (Smoking /
   * Alcohol / Drugs / Tobacco) so there's no add-new-entry endpoint, and no
   * user-facing trend (audit is kept server-side for compliance only). Two
   * POSTs: Add-flow for the full detail write, Update-flow for status changes.
   */
  addictionV2: {
    list:                 addictionV2API + "/api/v1/addictions/{0}",
    add:                  addictionV2API + "/api/v1/addictions/{0}/{1}",
    updateStatus:         addictionV2API + "/api/v1/addictions/{0}/{1}/status",
    trend:                addictionV2API + "/api/v1/addictions/{0}/{1}/trend",
    markHistoryIncorrect: addictionV2API + "/api/v1/addictions/{0}/{1}/history/{2}/incorrect",
  },
  /**
   * Allergen catalog — federated fuzzy-search served by api-allergy-v2-service.
   * The service fans out to api-medicine-service (drugs) + own allergenCatalog
   * (foods/other) and returns a unified list:
   *   { data: [{ id, name, category, mCode?, source }] }
   * source ∈ "medicine" | "catalog" | "free-text".
   */
  allergenCatalog: {
    search: allergyV2API + "/api/v1/allergen-catalog?search={0}&limit=100",
  },
  /**
   * Allergen static config — authoritative Category enum. Fetched once
   * by AllergyCategoriesService.js and cached at module scope. Backed
   * by api-allergy-v2-service's constants.AllCategories so widening
   * the taxonomy is a one-place change on the backend.
   *   { categories: string[] }
   */
  allergenConfig: {
    get: allergyV2API + "/api/v1/allergen-config",
  },
  /**
   * Allergy v2 (api-allergy-v2-service). Seventh v2 service. Full CRUD with
   * Allergen field locked in Edit (server preserves identity fields even if
   * a client sends them). No status/trend endpoints.
   */
  allergyV2: {
    list:                  allergyV2API + "/api/v1/allergies/{0}",
    add:                   allergyV2API + "/api/v1/allergies/{0}",
    update:                allergyV2API + "/api/v1/allergies/{0}/{1}",
    delete:                allergyV2API + "/api/v1/allergies/{0}/{1}",
    trend:                 allergyV2API + "/api/v1/allergies/{0}/{1}/trend",
    markHistoryIncorrect:  allergyV2API + "/api/v1/allergies/{0}/{1}/history/{2}/incorrect",
  },
  /**
   * Anthropometric v2 (api-anthropometric-v2-service). Tenth v2 service.
   * Fixed 7-parameter set; server auto-writes BMI on Weight/Height saves
   * and WHR on Waist/Hip saves. Flag computed server-side using patient
   * DOB + gender fetched from the monolith (fails hard if unreachable).
   */
  anthropometricV2: {
    list: anthropometricV2API + "/api/v1/anthropometrics/{0}",
    // Summary view — same endpoint with ?view=summary drops statusHistory from
    // the response. Landing-page overview card uses this for the 5-metric strip.
    summary: anthropometricV2API + "/api/v1/anthropometrics/{0}?view=summary",
    add: anthropometricV2API + "/api/v1/anthropometrics/{0}",
  },
  /**
   * Chief Complaints v2 (api-cc-v2-service). Section-by-section revamp — this is
   * the first of the v2 fleet. See project_cc_v2_service memory for the merge
   * cases (add / reactivate via ICD dedup / update-status auto-resolve).
   *
   * Every mutating endpoint takes `expectedVersion` in the body for optimistic
   * locking; a 409 comes back with the fresh doc so the caller can reconcile.
   */
  chiefComplaintsV2: {
    list:                  ccV2API + "/api/v1/complaints/{0}",
    add:                   ccV2API + "/api/v1/complaints/{0}",
    updateStatus:          ccV2API + "/api/v1/complaints/{0}/{1}/status",
    delete:                ccV2API + "/api/v1/complaints/{0}/{1}",
    trend:                 ccV2API + "/api/v1/complaints/{0}/{1}/trend",
    markHistoryIncorrect:  ccV2API + "/api/v1/complaints/{0}/{1}/history/{2}/incorrect",
    correctHistory:        ccV2API + "/api/v1/complaints/{0}/{1}/history/{2}/correct",
  },
  /**
   * Daily schedule v2 (api-daily-schedule-v2-service). Twelfth v2 service.
   * Timeline + hydration + sleep; per-entry StatusHistory audit; no-duplicate
   * rule on time AND activity name; soft delete.
   *
   * One document per patient PER CALENDAR DAY (mirrors the patient app's
   * routine reporting). {0} = patientId, {1} = entryId, and every day-scoped
   * route takes `?date=YYYY-MM-DD` ({2} on list / mutations, appended by
   * DailyScheduleService). `days` pages the filled days newest-first
   * (`?limit=&before=`); `template` is the default 14-row timeline the UI
   * offers on an unfilled day. The server resolves a missing date to today.
   */
  dailyScheduleV2: {
    template:        dailyScheduleV2API + "/api/v1/daily-schedule/template",
    list:            dailyScheduleV2API + "/api/v1/daily-schedule/{0}?date={1}",
    days:            dailyScheduleV2API + "/api/v1/daily-schedule/{0}/days",
    addEntry:        dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries?date={1}",
    updateEntry:     dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/{1}?date={2}",
    deleteEntry:     dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/{1}?date={2}",
    trendEntry:      dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/{1}/trend",
    batchEntries:    dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/batch?date={1}",
    updateHydration: dailyScheduleV2API + "/api/v1/daily-schedule/{0}/hydration?date={1}",
    updateSleep:     dailyScheduleV2API + "/api/v1/daily-schedule/{0}/sleep?date={1}",
  },
  /**
   * Diagnosis v2 (api-diagnosis-v2-service). Second v2 service — same merge
   * cases as CC (add / reactivate via ICD dedup / update-status / delete). Only
   * differences from CC: `ongoing bool` replaces the VAS numeric scale, and
   * each diagnosis carries linked `complaintIds` (real refs to CC v2 entries).
   *
   * Every mutating endpoint takes `expectedVersion` in the body for optimistic
   * locking; a 409 comes back with the fresh doc so the caller can reconcile.
   */
  diagnosisV2: {
    list: diagnosisV2API + "/api/v1/diagnoses/{0}",
    add: diagnosisV2API + "/api/v1/diagnoses/{0}",
    updateStatus: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/status",
    delete: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}",
    trend: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/trend",
    markHistoryIncorrect: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/history/{2}/incorrect",
    correctHistory: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/history/{2}/correct",
  },
  /**
   * Digitisation — called DIRECT against the services host (same pattern
   * bridge-frontend uses; no v2 proxy). Response shapes:
   *
   *   documentsForDigitisation → `[{document: {id, title, url, thumbnail,
   *     format, typeTitle, createdAt, updatedAt, ...}, appointment, service}, …]`
   *     Frontend flattens `document.*` into a picker-friendly `{id, name,
   *     url, thumbnail, ...}` for the modal.
   *
   *   triggerDigitisation → POST `{documentId: N}`. Digitisation runs async
   *     upstream; on any 2xx we treat it as "started" and refresh the
   *     section — the extracted values land shortly after.
   */
  digitisation: {
    documentsForDigitisation: serviceAPI + "/v1/documents/digitisation/{0}",
    triggerDigitisation: serviceAPI + "/v1/lab-report-digitisation/digitise-report",
    // Signed URL token needed to open documents in a new tab — the file
    // host rejects direct hits with 403. Bridge caches the response in
    // localStorage under `zy.bridge.doc.token`; we reuse the same key so
    // one token warms both apps.
    documentToken: baseAPI + "/documents/token/generate",
  },
  /**
   * Family History v2 (api-family-history-v2-service). Fifth v2 service —
   * user-appended relatives list with full CRUD. No status / no trend
   * (relatives are static records). The Update endpoint is a full-replace
   * of the mutable fields; server preserves AddedBy/AddedAt.
   */
  familyHistoryV2: {
    list: familyHistoryV2API + "/api/v1/family-history/{0}",
    add: familyHistoryV2API + "/api/v1/family-history/{0}",
    update: familyHistoryV2API + "/api/v1/family-history/{0}/{1}",
    delete: familyHistoryV2API + "/api/v1/family-history/{0}/{1}",
  },
  /**
   * Food preferences v2 (api-food-preferences-v2-service). Eleventh v2
   * service. Single-doc-per-patient; frontend debounces chip toggles into
   * a full-doc PUT. Enums come from /config (cached per session).
   * Ingredients proxied to api-recipes-service; allergens reuse Allergy v2.
   */
  foodPreferencesV2: {
    config:      foodPreferencesV2API + "/api/v1/food-preferences/config",
    get:         foodPreferencesV2API + "/api/v1/food-preferences/{0}",
    put:         foodPreferencesV2API + "/api/v1/food-preferences/{0}",
    ingredients: foodPreferencesV2API + "/api/v1/food-preferences/ingredients",
  },
  /**
   * Hospitalisation v2 (api-hospitalisation-v2-service). Eighth v2 service.
   * Full CRUD with cause (ICD-snapshot; frontend uses IcdAutocomplete against
   * api-icd-service directly) and multi-procedure (federated via the service's
   * own /procedure-catalog proxy).
   */
  hospitalisationV2: {
    list: hospitalisationV2API + "/api/v1/hospitalisations/{0}",
    add: hospitalisationV2API + "/api/v1/hospitalisations/{0}",
    update: hospitalisationV2API + "/api/v1/hospitalisations/{0}/{1}",
    delete: hospitalisationV2API + "/api/v1/hospitalisations/{0}/{1}",
  },
  /**
   * ICD-10 catalog search (api-icd-service). Backs the fuzzy-search dropdown in
   * the Chief complaints editor. Hits the services subdomain (NOT the api one
   * — the api subdomain 404s on this path). Response: CodeValuePair[].
   */
  icd: {
    // `searchRanked` returns hits ordered by match quality (exact →
    // starting → word → rest) against disease_chief_complaint /
    // common_terms / code, alphabetical inside each bucket. Backs the
    // four IcdAutocomplete callers: Chief complaints, Diagnosis,
    // Hospitalisation, Family history. `limit` is deliberately omitted
    // — the endpoint returns everything by default; add &limit=N if a
    // caller ever wants a cap.
    // (phr 2b61dac, ZY-10202 — synced 2026-09-30. The picker shows the rows
    // in the order they arrive, so the ranking is the server's alone.)
    search: serviceAPI + "/api/v1/dt/icd/searchRanked?queryString={0}",
  },
  /**
   * Lab investigation v2 (api-lab-investigation-v2-service). Ninth v2 service.
   * Full CRUD + batch endpoint for digitisation results. Server flags each
   * row from ref-range on save.
   */
  labInvestigationV2: {
    list: labInvestigationV2API + "/api/v1/lab-investigations/{0}",
    batch: labInvestigationV2API + "/api/v1/lab-investigations/{0}/batch",
    update: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}",
    delete: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}",
    /* The mark-incorrect / correction flow (synced from phr, 2026-09-14). */
    markIncorrect: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}/history/{2}/incorrect",
    correctHistory: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}/history/{2}/correct",
    /* SLT-code → qualifier-tag catalog (synced from phr, 2026-09-18). The
       manual-entry card caches it and offers a Tag only for sub-tests listed. */
    subtestTags: labInvestigationV2API + "/api/v1/lab-investigations/subtest-tags",
  },
  /**
   * Lab-test catalogue (dtlabtest, behind the services host like ICD).
   * Backs Create Rx's "Advised investigations" — the SAME two endpoints bridge's
   * card uses, so a test advised from phr carries the code a bridge-authored
   * prescription would.
   *
   *  search → [{ label: "Beta 2 Microglobulin, CSF (mass/ volume)",
   *              value: "LABT260" }]      (panel-level codes)
   *  info   → the catalogue row for one code; only `.instruction` is read, as
   *           the default advice to prefill when a test is picked.
   *
   * NOT api-lab-investigation-v2-service's `/labtest-catalog`: that returns
   * SUB-test `sltCode`s, a different code space no prescription stores.
   */
  /**
   * Create Rx (api-prescription-service).
   *
   * The SAME service bridge's own /prescription/:userId screen calls — these are
   * its endpoints, not new ones. Bridge's shared APIConfig names them on
   * `serviceAPINew`; this module uses `serviceAPI` because
   * EnvironmentConfig.js sets `serviceUrl === serviceNew` in all three
   * environments (uat / prod / dev1), so they resolve to one host. Kept on
   * serviceAPI to match the other 20 blocks here rather than introducing a
   * second base that means the same thing.
   *
   * `create` is bridge's `add`. There is no `draft` — this screen has no draft
   * state (product decision): it always builds fresh from the patient's records
   * and Publish is the only way out.
   */
  prescription: {
    latest: serviceAPI + "/api/v1/prescription/{0}",
    create: serviceAPI + "/api/v1/prescription/create",
    update: serviceAPI + "/api/v1/prescription/update",
    // Each published prescription's id, PDF document id (null without one) and doctor,
    // [{id, pdfId, doctorDetails: {name}}], newest first; ?hasPdf=true keeps only those with a
    // PDF — the name beside a prescription in the documents lists. {0}=patient id.
    doctors: serviceAPI + "/api/v1/prescription/doctors/{0}",
    /**
     * The patient's measured vitals — height / weight / BMI / weigh-in date —
     * used ONLY to prefill the Patient-details card. `dateOfWeightRecorded` is
     * REQUIRED by the save and has no other source. The response's `Reports`
     * half is deliberately ignored: the clinical cards come from the v2
     * services above. {1}=employee id, {2}=employee name.
     */
    personalDetails:
      serviceAPI + "/api/v1/prescription/tracking/rpd/{0}?userId={1}&userName={2}",
  },

  /**
   * The legacy Rx screen's publish events (prescription/helper/rxPublishEvents):
   * its event log (START_PRESCRIPTION / ERROR_PRESCRIPTION_* /
   * PUBLISH_PRESCRIPTION), and the patient's plan assignments, which its
   * CleverTap RX_PUBLISH event carries. Same paths as bridge's shared APIConfig
   * (`events`, `planAssignment`) and phr's. The shared config puts the event
   * log on serviceAPINew; `serviceUrl === serviceNew` in every environment
   * (see `prescription` above), so serviceAPI it is.
   */
  events: {
    log: serviceAPI + "/v1/events",
  },
  planAssignment: {
    fetchAllPlan: serviceAPI + "/v1/plans/assignments/patients/{0}?active=all",
  },

  labTest: {
    search: serviceAPI + "/api/v1/dt/labtest/search?queryString={0}",
    /** A panel's sub-tests MATCHING a query — `{0}` is the panel's LABT code. */
    subTests: serviceAPI + "/api/v1/dt/labtest/{0}/sublabtests/search?queryString={1}",
    /**
     * A panel's FULL child list — the same route without `/search`, which takes
     * no queryString at all. LABT31 answers 28 rows.
     *
     * The `/search` sibling's empty-query 400 says "Ltcode is null", which is a
     * copy-paste of its missing-path-param message; that wording is why this
     * route was long assumed not to exist. It does, and it is what makes an
     * honest "Panel · 28 tests" possible. Empty answers 204 with NO BODY.
     */
    children: serviceAPI + "/api/v1/dt/labtest/{0}/sublabtests",
    /**
     * Children for MANY panels in one round-trip — POST `{ltCodes:[…]}`, answers
     * `[{ltCode, testName, subtest:[{sltCode, subTestName, …}]}]`. Replaces a
     * per-panel fan-out. Note the nested key is `sltCode` (camel) where the
     * single-panel route spells it `sltcode`.
     */
    childrenBulk: serviceAPI + "/api/v1/dt/labtest",
    /**
     * Global sub-test search by CANONICAL name, unscoped to a panel. Its
     * projection drops the parent (`LtCode` is in the DAO's hands and thrown
     * away before serialising), so rows arrive `{label, value}` only — pair it
     * with the synonym map / bulk resolve for panel + unit.
     */
    subTestByName: serviceAPI + "/api/v1/dt/labtest/search/subtest/name?queryString={0}",
    info: serviceAPI + "/api/v1/dt/labtest/getby/{0}",
    withSubTestGetAll: serviceAPI + "/api/v1/dt/labtestwithsubtest/getall",
    subTestOnSelect: serviceAPI + "/api/v1/dt/labtest/{0}/sublabtests/onselect/{1}",
  },
  /**
   * The lab-test SYNONYM map (api-labtest-service, Mongo side collection —
   * a DIFFERENT store from the MySQL catalogue above).
   *
   * It is the only place two things exist:
   *  - the REVERSE LINK. A row carries `mainTestCode`/`mainTestName`, so
   *    searching "hem" surfaces Hemoglobin *and* the 14 panels containing it —
   *    which the catalogue's own sub-test search cannot do (it drops the parent
   *    before serialising).
   *  - the AUTHORITATIVE UNIT. `unitRef` is the REFLD code itself, so a result's
   *    unit no longer has to be recovered by fuzzy-matching its label.
   *
   * Coverage is PARTIAL BY CONSTRUCTION: rows are hand-seeded and the service
   * refuses to create a synonym unless a unit already exists for that test. Use
   * it to enrich, never as the search of record. Empty answers 200 with body
   * `null` (not 204, and not `[]`). `isLabTest` defaults to false server-side —
   * pass it explicitly. Panels never carry a unit: every write hardcodes
   * `isLabTest:false`.
   */
  labTestMapping: {
    search: serviceAPI + "/api/v1/dt/labtestmapping/search?queryString={0}&isLabTest=false",
    bySubTest: serviceAPI + "/api/v1/dt/labtestmapping/getby/sltcode/{0}",
  },
  /**
   * Medicine catalog (api-medicine-service). Fuzzy-search endpoint used by
   * the Medicine v2 add/edit form to pick a drug. Returns
   *   { data: [{ id, mCode, medicineName, manufacturerName, formulation, ... }],
   *     total, page, limit }
   * — the frontend snapshots the picked entry into `medicineCode` on the
   * medicine record. Hits the services subdomain (same host as ICD search).
   */
  medicineCatalog: {
    // `sort=matchRank` opts this caller into api-medicine-service's
    // match-quality ranked search (exact → starting → word → rest,
    // alphabetical inside each bucket). Server-side hard cap 2000 so
    // limit is set high enough to expose the whole catalog for the
    // ranker to sort — other callers that don't send matchRank keep
    // their existing dateCreated-DESC behaviour untouched.
    // (phr 7ac8415, ZY-10202 — synced 2026-09-30.)
    search: serviceAPI + "/api/v1/medicine?search={0}&sort=matchRank&limit=2000",
    /* The formulation catalogue behind the medicine form's units and
       instructions (synced from phr, 2026-09-14). */
    formulations: serviceAPI + "/api/v1/medicine/formulations",
    formulationByCode: serviceAPI + "/api/v1/medicine/formulations/{0}",
  },
  /**
   * Medicine v2 (api-medicine-v2-service). Third v2 service — same merge
   * pattern as CC + Diagnosis but with a three-state status (ongoing /
   * completed / discontinued) and a richer prescription-shaped state
   * (dose / frequency / duration / instructions). Dedup key is
   * `medicineCode.mCode` from the catalog.
   */
  medicineV2: {
    list: medicineV2API + "/api/v1/medicines/{0}",
    add: medicineV2API + "/api/v1/medicines/{0}",
    updateStatus: medicineV2API + "/api/v1/medicines/{0}/{1}/status",
    delete: medicineV2API + "/api/v1/medicines/{0}/{1}",
    trend: medicineV2API + "/api/v1/medicines/{0}/{1}/trend",
    adherenceReview: medicineV2API + "/api/v1/medicines/{0}/{1}/adherence",
  },
  /**
   * Procedure catalog — pure proxy served by api-hospitalisation-v2-service to
   * the upstream api-hospitalisation-service /surgeries endpoint. Same
   * envelope as the other v2 catalog clients: `{data: [{code, name}]}`.
   * Uses `query` (not `search`) to match the upstream contract.
   */
  procedureCatalog: {
    search: hospitalisationV2API + "/api/v1/procedure-catalog?query={0}",
    /* The whole CPT catalogue, loaded once and filtered locally — what phr's
       ProcedureCatalogAllService replaced the per-keystroke search with
       (phr 1276773, ZY-10311). */
    all: hospitalisationV2API + "/api/v1/procedure-catalog/all",
  },
  /**
   * Progress notes. Ported verbatim from bridge (`newProgressNote` /
   * `progressNoteComment` / `progressNoteTaskZoho`). This is an Eve-style Mongo
   * REST API, not the plain REST the rest of phr talks to, so the query strings
   * are load-bearing:
   *   where=      a JSON filter object   {"patientId":N} / {"patientId":N,"tag":T}
   *   embedded=   {"comments":1} inlines each note's comments (no N+1)
   *   max_results / page   paging (bridge asks for 15 a page)
   *   sort=[("_id",-1)]    newest first
   *   time=       cache-buster, a plain Date.now()
   * The list responds { items: [...] } — NOT Eve's default `_items`.
   *
   * Mind the {n} slots, they are NOT in call order: getAll takes
   * (patientId, maxResults, page, time) and getAllWithType
   * (patientId, maxResults, page, tag, time). `search` only substitutes {0} and
   * {4} — it filters on patientId + a body regex and ignores tag AND paging.
   * That is bridge's shape; bridge compensates by resetting the active tag to
   * ALL whenever a search runs, so the results never contradict the chips.
   */
  progressNotes: {
    new: serviceAPI + "/api/v1/progress_notes",
    getAll:
      serviceAPI +
      '/api/v1/progress_notes?where={"patientId":{0}}&embedded={"comments":1}&max_results={1}&page={2}&sort=[("_id",-1)]&time={3}',
    getAllWithType:
      serviceAPI +
      '/api/v1/progress_notes?where={"patientId":{0},"tag":{3}}&embedded={"comments":1}&max_results={1}&page={2}&sort=[("_id",-1)]&time={4}',
    update: serviceAPI + "/api/v1/progress_notes/{0}",
    // One note, comments inlined. Lets a card refresh itself after a comment or
    // an acknowledgement without re-pulling (and collapsing to) page 1.
    getOne: serviceAPI + '/api/v1/progress_notes/{0}?embedded={"comments":1}&time={1}',
    search:
      serviceAPI +
      "/api/v1/progress_notes?where={%22patientId%22:{0},%20%22body%22:%20{%22%24regex%22:%20%20%22{4}%22,%20%20%22%24options%22:%20%22i%22}}&embedded={%22comments%22:1}&sort=[(%22_id%22,-1)]",
  },
  progressNoteComment: {
    new: serviceAPI + "/api/v1/comments",
    update: serviceAPI + "/api/v1/comments/{0}",
  },
  /* Tasks go to ZOHO, matching phr. Bridge's shared config carries an ASANA
     block under this same name with no consumers, plus a Zoho block its legacy
     progress-notes screen uses; neither is touched. */
  progressNoteTask: {
    newTask: serviceAPI + "/v2/tm/project/task",
    projects: serviceAPI + "/v2/tm/projects",
    usersByProject: serviceAPI + "/v2/tm/users/{0}",
  },

  /* ---------- health plan (v4) ----------
     Bridge already has every one of these URLs, but under different key names
     and with one differing query string (its list hardcodes pageSize=2, since
     its own dropdown shows the last two versions; the history panel wants the
     real list and the server honours a larger page). Restated here under the
     names the ported service uses, so the module stays deletable and bridge's
     shared config stays untouched. */
  healthPlan: {
    latest: serviceAPI + "/v4/hp/{0}/latest",
    byId: serviceAPI + "/v4/hp/{0}/{1}",
    // Bridge hardcodes pageSize=2 (its dropdown shows only the last two);
    // phr's history panel wants the real list, and the server honours a
    // larger page.
    list: serviceAPI + "/v4/hp/list/{0}?pageNo=1&pageSize=50",
    create: serviceAPI + "/v4/hp/{0}",
    update: serviceAPI + "/v4/hp/{0}/{1}",
    categories: serviceAPI + "/v4/category",
    itemsByCategory: serviceAPI + "/v4/item/category/{0}",
    optionSearch: serviceAPI + "/v4/option/search/opt/{0}?query={1}",
    optionDetail: serviceAPI + "/v4/option/detail/{0}",
    guidelinesByCategory: serviceAPI + "/v1/guidelines/cat/{0}",
    ingredientUnits: serviceAPI + "/v1/ingredients/units",
    // The ingredient master list behind a recipe's "+ Add ingredient" (bridge's
    // recipeDashboard.ingredient). Responds a bare array of { id, name, category }.
    ingredientsAll: serviceAPI + "/v1/ingredients/all",
    // Auto-generate the nutrition plan ({0}=patientId, {1}=employeeId) — the
    // options it creates come back with auto:true. `?source=v2` opts this
    // generate call into hp-automation's v2 tracking + DA fetches (per-request
    // switch on top of the deployment-level env defaults), as phr's does
    // (ZY-10331). Bridge's OWN health-plan screen — new-bridge's
    // NewPatientHealthPlan via healthPlanDashboard.generateHp — is a different
    // caller and still takes whatever the environment defaults to.
    generate: serviceAPI + "/v4/hp/nutrition/{0}/{1}?source=v2",
    // Truthy = migrated to the v4 model; falsy = still on the legacy plan
    // (bridge's newHealthplanStatus check).
    migrationCheck: serviceAPI + "/v1/hpmgrn/check/{0}",
    // The patient's food list ({1}=filter, bridge sends "all") — responds
    // { recommendedFoodList, allowedFoodList, limitedFoodList,
    //   notAllowedFoodList } of { foodItemName, foodItemType, ... }.
    foodList: serviceAPI + "/v1/foodlist/{0}/latest?filter={1}",
    // Kick off PDF generation after publish; body { patientId, healthPlanId,
    // author: { name, employeeId } }. Poll pdfStatus until { pdfURL } appears —
    // bridge gates the publish notifications on the PDF being ready.
    pdfGenerate: serviceAPI + "/v1/hp-pdf",
    pdfStatus: serviceAPI + "/v1/hp-pdf/{0}/{1}",
    // Section templates (legacy FetchPlan, on Exercise / Medical / Meditation):
    // search a category's templates by name → [{ id, name }]; one template →
    // { id, name, category, data: { [categoryId]: [{ id, name, options, time }] } }.
    templateSearch: serviceAPI + "/api/v1/hp/template/search/{0}?query={1}",
    templateById: serviceAPI + "/api/v1/hp/template/{0}",
    // Suggestions for a recipe ingredient's instruction (legacy Recipe's
    // getinstruction) → [{ id, name }].
    instructionSearch: serviceAPI + "/v1/instructions?filter={0}",
  },
  /**
   * The rest of bridge's growthEvents (production, once the PDF is ready): the
   * CRM "tentacle" disposition for the patient, and the member mapping behind
   * bridge's 02XXAB CleverTap A/B event.
   */
  tentacle: {
    disposition: serviceAPI + "/api/v1/web-hook/health-assessment/tentacle",
    fetchDetails: serviceAPI + "/api/v1/web-hook/health-assessment/disposition?phone={0}",
    memberMapping: serviceAPI + "/v1/patients/mapping/{0}",
  },
  whatsapp: {
    event: serviceAPI + "/api/v1/zylawhatsapp/event",
    short: serviceAPI + "/api/v1/zylawhatsapp/short",
  },
  sms: {
    send: serviceAPI + "/api/v1/patient-communication/sms",
  },

  /* ---------- legacy medication tracking (the "Old" medicines view) ----------
     Bridge's own Tracking 2.0 panel reads these
     (new-bridge/services/TrackingService.js:36-48). READ-ONLY here: the `add`
     key that block also carries is deliberately not restated, so this view
     cannot write even by accident. */
  tracking: {
    medicine: {
      count: serviceAPI + "/api/v1/tracking/md/{0}/count",
      list: serviceAPI + "/api/v1/tracking/md/{0}?pageSize={1}&pageNo={2}",
    },
  },
  /* The salt unit-of-measure master. getSaltTherapy resolves each composition's
     `uom` through it, and it is keyed by `code` — NOT `value` like the
     dataTables lists. */
  medicineSalt: serviceAPI + "/api/v1/dt/medicines/saltUom",
  /* The shared reference lists (MEDICINE_FORMULATIONS / MEDICINE_INSTRUCTIONS /
     MEDICINE_TIMEPERIOD), keyed by `value`. */
  /**
   * Address book — the patient's alternate contact numbers.
   *
   * The SAME three endpoints bridge already exposes as
   * `APIConfig.alternatePhoneNo` (src/service/network/APIConfig.js:813-817) and
   * already consumes from the legacy phone-number screen
   * (new-bridge/components/PhoneNo.js) and from CallButton, which lists these
   * numbers so an operator can pick one to ring. Repeated here rather than
   * reached across because this module owns its config and stays deletable.
   *
   * GET  -> [{ id, patientId, type, alternateNumber, countryCode, areaCode,
   *            description }]. An empty array is a normal state.
   * POST -> body { patientId, countryCode:int, areaCode, alternateNumber,
   *                type, description }.
   * PUT  -> the same fields plus `id`; {1} is that id.
   */
  addressBook: {
    list:   baseAPI + "/patients/{0}/pan",
    add:    baseAPI + "/patients/{0}/pan",
    update: baseAPI + "/patients/{0}/pan/{1}",
  },

  referenceLists: serviceAPI + "/api/v1/dt/referenceList/getAllDetails",

  /**
   * The header's "All documents · View all" (components/DocumentsMenu,
   * UploadDocumentModal) — phr APIConfig.documentApis, KEY FOR KEY.
   *
   * The same paths sit in bridge's shared APIConfig.documentApis, but that one
   * names the rename `updateNameAndDescription`; the vendored service reads
   * phr's `updateName`, and apiconfig-parity compares by phr's key path.
   *
   * getByPatientDoc -> [{ type, count, all: [document] }] (every document the
   *                    patient holds, grouped by folder type)
   * tokenGenerate   -> { token, expiredTime } — appended to file URLs as ?token=
   * uploadDoc       -> multipart: file, fileName, patientId, document (JSON string)
   * updateType      -> PATCH { type }
   * updateName      -> PATCH { title, description } — BOTH every time; the
   *                    server assigns each unconditionally.
   */
  documentApis: {
    getByPatientDoc: serviceAPI + "/v1/documents/patients/{0}?groupBy=type",
    tokenGenerate: baseAPI + "/documents/token/generate",
    uploadDoc: serviceAPI + "/v1/documents",
    updateType: serviceAPI + "/v1/documents/{0}/type",
    updateName: serviceAPI + "/v1/documents/{0}/name",
  },

  /* Call notes. PATIENT-SCOPED, which is the whole reason this works on bridge
     and did not on phr: phr anchors a note to a call it finds by matching an
     APPOINTMENT's date, and this route has no appointment. Bridge resolves the
     patient's own call plan over a date window instead, so the anchor comes
     from the patient id the screen already holds.
     Same URLs bridge's own call-plan screen uses (its APIConfig call/callNote
     blocks); restated here so the module stays deletable. */
  call: {
    getCallPlanByCallee:
      baseAPI + "/calls/callplan/callee?callee={0}&fromDate={1}&toDate={2}",
  },
  callNote: {
    new: baseAPI + "/calls/notes/new",
    getAllByCall: baseAPI + "/calls/notes/calls/{0}",
    update: baseAPI + "/calls/notes/{0}",
    /* Pushes a SAVED note into the patient's chat. The body carries the note's
       ID as a string, not its text, so the note must exist first. */
    directMessage: serviceAPI + "/v1/chat/users/patients/{0}/message",
    /* Marks a note as sent so it cannot be pushed twice. Irreversible. */
    disable: baseAPI + "/calls/notes/disable/{0}",
  },

  /**
   * Drug schedule — pharmacy-dose-schedule-api, the same routes phr calls.
   * Restated here rather than imported so this module stays deletable.
   *
   * `pid` is the query-param name the existing controller reads. The list
   * answers `[{_id, pid, status:"Active"|"Completed"|"Expired", doses:[...]}]`
   * with status CAPITALISED and `nextdosefrequency.value` a STRING, not an int.
   * `criticalinstruction` on the catalogue is HTML and is sanitised before it
   * is rendered.
   */
  drugSchedule: {
    drugCatalog: serviceAPI + "/api/v1/nv/drug",
    listByPatient: serviceAPI + "/api/v1/nv/dose/get?pid={0}",
    upcomingByPatient: serviceAPI + "/api/v1/nv/dose/upcomingdosedate?pid={0}",
    create: serviceAPI + "/api/v1/nv/dose/create",
    update: serviceAPI + "/api/v1/nv/dose/update",
  },

  /**
   * Call notes, v2 — Mongo, keyed on (patientId, appointmentId). Used by
   * CallNoteModal, opened from the appointments board's row menu with that
   * row's appointment (2026-09-21). A v1 note hung off a hand-authored CALL PLAN
   * row, which is why the old flow made the operator pick one; v2 is keyed on
   * ids the board already has, so there is nothing to pick.
   *
   * IDS: a NUMBER for notes created from 2026-10-14 (1,000,000,001 and up,
   * api-calls-service's call_notes_v2 counter), a 24-char hex ObjectID before
   * that; both answered as text. The chat push (`callNote.directMessage` above)
   * carries the id as a string either way. The released patient app opens a
   * numeric id through the v1 details route; a newer build picks its endpoint
   * from the id's shape.
   */
  callNoteV2: {
    /** POST `{ patientId, appointmentId, doctorId, content }` -> the saved note. */
    new: baseAPI + "/v2/calls/notes/new",
    /** The notes on one (patient, appointment) pair, newest first. BARE ARRAY. */
    byAppointment: baseAPI + "/v2/calls/notes/patient/{0}/appointment/{1}",
    /** PUT, body ignored. Idempotent. */
    disable: baseAPI + "/v2/calls/notes/disable/{0}",
  },

  /**
   * Primary-therapy assessment — the header's condition chip, and the dropdown
   * that changes it. pa-service, on serviceAPI.
   *
   * `primary` answers an int code ({ primaryTherapy: 2, answer: "2" });
   * `questions` is the code -> name master ({ fields: [{ id, tag, text }, …] }).
   * `changeTherapy` is a GENERIC assessment write, so `questionId: "1"` is not
   * optional — 1 is the primary-therapy question.
   *
   * Restated here rather than reached through
   * ops/.../PatientsService.getPrimaryTherapyInfo, which is where bridge reads
   * this today. That method is not broken — it uses the hardcoded prod host
   * only when isProduction() and the env config otherwise — but the comment in
   * PatientIdentityBand claimed it read prod data from every environment, which
   * is why the chip was omitted here in the first place. Owning the URLs in the
   * module's own config settles it and keeps the module deletable.
   */
  patientAssessment: {
    primaryTherapy: serviceAPI + "/v2/pa/assessments/{0}/primary",
    therapyQuestions: serviceAPI + "/v2/pa/assessments/questions/1",
    changeTherapy: serviceAPI + "/v2/pa/assessments/{0}",

    /**
     * Expected delivery date — required before a patient can be put on the
     * Pregnancy therapy, which is why it lives beside these.
     *
     * `read` is templated on the patient; `write` is ONE url for both verbs,
     * POST to add and PUT to update. The two bodies are NOT the same shape:
     * add sends a bare "YYYY-MM-DD", update sends it with a "T00:00:00.000Z"
     * suffix. That asymmetry is bridge's (TherapyInputDialog.onHandleSave) and
     * is reproduced deliberately rather than tidied — the endpoint has been
     * fed exactly this for years and nothing here can prove it tolerates the
     * other form.
     */
    deliveryDateRead: serviceAPI + "/v2/pa/assessments/deliverydate/{0}",
    deliveryDateWrite: serviceAPI + "/v2/pa/assessments/deliverydate",

    /**
     * Minor members on the account. POST { patientIds: [id] } -> an array.
     * The New born care therapy needs one to exist; on family-service
     * (/v1/family), not pa-service.
     */
    minorMembers: serviceAPI + "/v1/family/getminormembers",
  },

  /*
   * Risk stratification — the overview card's risk pill + narrative.
   *
   * Bridge's APP-LEVEL APIConfig declares this service too (riskStratification
   * at service/network/APIConfig.js), but with a DIFFERENT call convention: it
   * lists bare `/api/v1/risk/detail` and `/api/v1/risk/history` and appends the
   * ids itself. These are phr's templated forms, restated here so the module
   * keeps owning its own config and stays deletable in one directory. Do not
   * "de-duplicate" them against the app-level block — the shapes differ.
   */
  riskStratification: {
    history: serviceAPI + "/api/v1/risk/history/{0}",
    /**
     * Trigger a fresh stratification for a patient, attributed to a doctor.
     * A GET that MUTATES — it queues a new assessment rather than reading one —
     * so it is only ever fired from an explicit click, never from an effect.
     * `{1}` is the logged-in doctorProfile.id.
     */
    stratify: serviceAPI + "/api/v1/risk/stratify/{0}/{1}",
    detail: serviceAPI + "/api/v1/risk/detail/{0}?includePrevious=true",
  },

  /**
   * Sleep + step tracker — served by api-sleep-step-tracker-service. Byte-for-
   * byte the URL bridge's own APIConfig already declares as `getSteps`.
   * `from` and `to` are RFC3339 ISO timestamps (`YYYY-MM-DDTHH:mm:ss.sssZ`).
   * Response is
   *   { patientId, steps: { [ISO]: count }, average, streak }
   * where `steps` is a map keyed by the day's UTC midnight — days with no
   * recorded value are simply ABSENT from the map, not zero.
   */
  stepTracker: {
    getSteps: serviceAPI + "/api/v1/sleep-step-tracker/steps/{0}?from={1}&to={2}",
  },

  /* Only the three keys this module reads. `doctorsGetAll` is baseAPI, NOT
     serviceAPI — a different host; getting that wrong 404s silently on the
     "recorded by" names. */
  user: {
    /* Resolves a referral code to a doctor, for the publish-time referral
       feedback (production-gated). */
    verifyDocCode: baseAPI + "/docprofile/doctor?code={0}",
  },

  /* Allergy reactions picker (synced from phr, 2026-09-14). The endpoint must be
     live in bridge's environment for the new reactions UI to populate. */
  reactionsCatalog: {
    list: allergyV2API + "/api/v1/reactions-catalog",
  },

  /* Lab units lookup (LabUnitsService). Singular, and distinct from the
     `referenceLists` string above. */
  referenceList: {
    getByName: serviceAPI + "/api/v1/dt/referenceList/getByName?name={0}",
  },

  /* The referral Specialty catalogue. prescription/helper/referralSpecialties.js
     used to reach bridge's SHARED service/network/APIConfig for these, which is
     what apiconfig-parity failed on ("no ported file reaches bridge's shared
     APIConfig"); it reads the module-local config now. Only the three keys it
     dereferences. */
  myPlan: {
    createService: {
      getServiceType: serviceAPI + "/v1/service/type?status={0}",
      getServiceSubType: serviceAPI + "/v1/service/subtype/type/{0}?status={1}",
    },
    serviceDashboard: {
      fetchServicesByFilter: serviceAPI + "/v1/service/service/filter",
    },
    /* The header's Patient-details dialog (service/PatientService). Same key
       path as phr's, so the service method matches phr's line for line. */
    navigo: {
      updateProfileInfo: serviceAPI + "/v1/patients/profile/update",
      // The patient's WORK email — not on the patient record; it lives in
      // api-client-config-service. Read-only here (the Patient-details
      // dialog); 404 "no work email found for patient" when there is none.
      getWorkEmail: serviceAPI + "/v1/account/patient/work-email/patient/{0}",
    },
  },
  /**
   * Prescription verification — legacy's "Verify Patient" record
   * (api-user-service GET|PUT /patients/{id}/verify, api/patient.go:1093-1172).
   * GET -> { status, comment, doctor } and 404s while the patient has no
   * verification record; PUT takes { status, commentType } and needs one.
   */
  prescriptionVerification: {
    get: baseAPI + "/patients/{0}/verify",
    update: baseAPI + "/patients/{0}/verify",
  },
  /**
   * The patient's referring doctor, set from a doctor code — legacy's "Doctor
   * Information" save (PatientsService.updateReferrer, over the same path as
   * bridge's shared APIConfig.patient.referrer). Read only by
   * service/DoctorCodeService.js; phr has no such key.
   */
  patient: {
    referrer: baseAPI + "/patients/{0}/referrer",
  },
};

export default PatientAPIConfig;
